import { IconPen, IconPlusRounded, IconTrash } from "@aksara-ui/icons";
import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogProps,
  IconButton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getQuestCheckpoints,
  getQuestBadges,
} from "redux/actions/quest";
import CheckpointFormModal from "./CheckpointFormModal";
import CheckpointConfirmModal from "./CheckpointConfirmModal";
import BadgeFormModal from "./BadgeFormModal";
import BadgeConfirmModal from "./BadgeConfirmModal";

interface Props {
  mission: any;
}

const MissionDetailPanel: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  mission,
}) => {
  const dispatch = useDispatch();
  const { questCheckpoints = [], questBadges = [] } = useSelector(
    (state: any) => state.questReducer ?? {}
  );

  const checkpointsList = Array.isArray(questCheckpoints) ? questCheckpoints : [];
  const badgesList = Array.isArray(questBadges) ? questBadges : [];

  const missionId = String(mission?.id ?? "");

  // Checkpoint modal state
  const [isCheckpointFormOpen, setIsCheckpointFormOpen] = useState(false);
  const [isCheckpointConfirmOpen, setIsCheckpointConfirmOpen] = useState(false);
  const [selectedCheckpointId, setSelectedCheckpointId] = useState("");

  // Badge modal state
  const [isBadgeFormOpen, setIsBadgeFormOpen] = useState(false);
  const [isBadgeConfirmOpen, setIsBadgeConfirmOpen] = useState(false);
  const [selectedBadgeId, setSelectedBadgeId] = useState("");

  useEffect(() => {
    if (isOpen && missionId) {
      dispatch(getQuestCheckpoints(missionId) as any);
      dispatch(getQuestBadges(missionId) as any);
    }
  }, [isOpen, missionId, dispatch]);

  const handleClose = () => {
    onClose && onClose();
  };

  const tierBadgeVariant = (tier: string) => {
    switch (tier?.toLowerCase()) {
      case "gold": return "warning";
      case "silver": return "default";
      case "bronze": return "default";
      case "platinum": return "info";
      default: return "default";
    }
  };

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={handleClose}
        overflowY={"auto"}
        style={{ maxWidth: 800, width: "90vw" }}
      >
        <DialogHeader>
          <Box>
            <Text fontWeight={700} scale={500}>
              {mission?.name ?? "Detail Quest"}
            </Text>
            <Text scale={300} color={theme.colors.grey06}>
              {mission?.description}
            </Text>
          </Box>
        </DialogHeader>
        <DialogContent>
          <Tabs>
            <TabList
              backgroundColor={"white"}
              borderBottom={`1px solid ${theme.colors.grey03}`}
              style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0 }}
            >
              <Tab>Checkpoints ({checkpointsList.length})</Tab>
              <Tab>Badge Tiers ({badgesList.length})</Tab>
            </TabList>
            <TabPanels>
              {/* ─── CHECKPOINTS TAB ─────────────────────────────────── */}
              <TabPanel>
                <Box paddingY={16}>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    marginBottom={12}
                  >
                    <Button
                      variant="primary"
                      size={32}
                      style={{ fontWeight: 600 }}
                      onClick={() => {
                        setSelectedCheckpointId("");
                        setIsCheckpointFormOpen(true);
                      }}
                    >
                      <IconPlusRounded size={16} fill="white" />
                      &nbsp;Tambah Checkpoint
                    </Button>
                  </Box>

                  <Card>
                    <Box overflowX="auto">
                      <Table>
                        <TableHead>
                          <TableHeadRow>
                            <TableHeadCell style={{ width: 60 }}>#</TableHeadCell>
                            <TableHeadCell>Nama</TableHeadCell>
                            <TableHeadCell>Kode Scan</TableHeadCell>
                            <TableHeadCell>Poin</TableHeadCell>
                            <TableHeadCell>Status</TableHeadCell>
                            <TableHeadCell textAlign="center">Aksi</TableHeadCell>
                          </TableHeadRow>
                        </TableHead>
                        <TableBody>
                          {checkpointsList.length === 0 ? (
                            <TableBodyRow>
                              <TableBodyCell colSpan={6} textAlign="center">
                                <Text scale={300} color={theme.colors.grey06}>
                                  Belum ada checkpoint
                                </Text>
                              </TableBodyCell>
                            </TableBodyRow>
                          ) : (
                            [...checkpointsList]
                              .sort((a: any, b: any) => a.sequence - b.sequence)
                              .map((cp: any) => (
                                <TableBodyRow key={cp.id}>
                                  <TableBodyCell>
                                    <Badge size="sm" variant="default">
                                      {cp.sequence}
                                    </Badge>
                                  </TableBodyCell>
                                  <TableBodyCell>{cp.name}</TableBodyCell>
                                  <TableBodyCell>
                                    <Badge size="sm" variant="info">
                                      {cp.code}
                                    </Badge>
                                  </TableBodyCell>
                                  <TableBodyCell>
                                    <Badge size="sm" variant="success">
                                      {(cp.points ?? 0).toLocaleString()} Pts
                                    </Badge>
                                  </TableBodyCell>
                                  <TableBodyCell>
                                    <Badge
                                      size="sm"
                                      variant={cp.is_active ? "success" : "default"}
                                    >
                                      {cp.is_active ? "Aktif" : "Nonaktif"}
                                    </Badge>
                                  </TableBodyCell>
                                  <TableBodyCell textAlign="center">
                                    <Stack
                                      display="flex"
                                      flexDirection="row"
                                      spacing="xs"
                                      justifyContent="center"
                                    >
                                      <IconButton
                                        onClick={() => {
                                          setSelectedCheckpointId(String(cp.id));
                                          setIsCheckpointFormOpen(true);
                                        }}
                                      >
                                        <IconPen fill={theme.colors.blue06} />
                                      </IconButton>
                                      <IconButton
                                        onClick={() => {
                                          setSelectedCheckpointId(String(cp.id));
                                          setIsCheckpointConfirmOpen(true);
                                        }}
                                      >
                                        <IconTrash fill={theme.colors.red05} />
                                      </IconButton>
                                    </Stack>
                                  </TableBodyCell>
                                </TableBodyRow>
                              ))
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Card>
                </Box>
              </TabPanel>

              {/* ─── BADGES TAB ──────────────────────────────────────── */}
              <TabPanel>
                <Box paddingY={16}>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    marginBottom={12}
                  >
                    <Button
                      variant="primary"
                      size={32}
                      style={{ fontWeight: 600 }}
                      onClick={() => {
                        setSelectedBadgeId("");
                        setIsBadgeFormOpen(true);
                      }}
                    >
                      <IconPlusRounded size={16} fill="white" />
                      &nbsp;Tambah Badge
                    </Button>
                  </Box>

                  <Card>
                    <Box overflowX="auto">
                      <Table>
                        <TableHead>
                          <TableHeadRow>
                            <TableHeadCell>Nama Badge</TableHeadCell>
                            <TableHeadCell>Tier</TableHeadCell>
                            <TableHeadCell>Min. Kunjungan</TableHeadCell>
                            <TableHeadCell>Reward Points</TableHeadCell>
                            <TableHeadCell>Status</TableHeadCell>
                            <TableHeadCell textAlign="center">Aksi</TableHeadCell>
                          </TableHeadRow>
                        </TableHead>
                        <TableBody>
                          {badgesList.length === 0 ? (
                            <TableBodyRow>
                              <TableBodyCell colSpan={6} textAlign="center">
                                <Text scale={300} color={theme.colors.grey06}>
                                  Belum ada badge tier
                                </Text>
                              </TableBodyCell>
                            </TableBodyRow>
                          ) : (
                            badgesList.map((badge: any) => (
                              <TableBodyRow key={badge.id}>
                                <TableBodyCell>{badge.name}</TableBodyCell>
                                <TableBodyCell>
                                  <Badge
                                    size="sm"
                                    variant={tierBadgeVariant(badge.tier)}
                                  >
                                    {badge.tier}
                                  </Badge>
                                </TableBodyCell>
                                <TableBodyCell>{badge.min_visits}x</TableBodyCell>
                                <TableBodyCell>
                                  <Badge size="sm" variant="success">
                                    {(badge.reward_points ?? 0).toLocaleString()} Pts
                                  </Badge>
                                </TableBodyCell>
                                <TableBodyCell>
                                  <Badge
                                    size="sm"
                                    variant={badge.is_active ? "success" : "default"}
                                  >
                                    {badge.is_active ? "Aktif" : "Nonaktif"}
                                  </Badge>
                                </TableBodyCell>
                                <TableBodyCell textAlign="center">
                                  <Stack
                                    display="flex"
                                    flexDirection="row"
                                    spacing="xs"
                                    justifyContent="center"
                                  >
                                    <IconButton
                                      onClick={() => {
                                        setSelectedBadgeId(String(badge.id));
                                        setIsBadgeFormOpen(true);
                                      }}
                                    >
                                      <IconPen fill={theme.colors.blue06} />
                                    </IconButton>
                                    <IconButton
                                      onClick={() => {
                                        setSelectedBadgeId(String(badge.id));
                                        setIsBadgeConfirmOpen(true);
                                      }}
                                    >
                                      <IconTrash fill={theme.colors.red05} />
                                    </IconButton>
                                  </Stack>
                                </TableBodyCell>
                              </TableBodyRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Card>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* ─── Checkpoint Modals ──────────────────────────────────────────── */}
      <CheckpointFormModal
        isOpen={isCheckpointFormOpen}
        onClose={() => setIsCheckpointFormOpen(false)}
        missionId={missionId}
        checkpointId={selectedCheckpointId}
      />
      <CheckpointConfirmModal
        isOpen={isCheckpointConfirmOpen}
        onClose={() => setIsCheckpointConfirmOpen(false)}
        checkpointId={selectedCheckpointId}
        missionId={missionId}
      />

      {/* ─── Badge Modals ───────────────────────────────────────────────── */}
      <BadgeFormModal
        isOpen={isBadgeFormOpen}
        onClose={() => setIsBadgeFormOpen(false)}
        missionId={missionId}
        badgeId={selectedBadgeId}
      />
      <BadgeConfirmModal
        isOpen={isBadgeConfirmOpen}
        onClose={() => setIsBadgeConfirmOpen(false)}
        badgeId={selectedBadgeId}
        missionId={missionId}
      />
    </>
  );
};

export default MissionDetailPanel;
